import React, { useState, useEffect } from 'react';
import '../App.css';

function Comparison({ savedCalculations = [], savedExpenses = [], proposalsApiUrl = '' }) {
  const [reports, setReports] = useState([]);
  const [filteredReports, setFilteredReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingProposals, setLoadingProposals] = useState(false);
  const [proposals, setProposals] = useState({});
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [filters, setFilters] = useState({
    city: '',
    state: '',
    clientName: '',
    projectNumber: '',
    freeDeliveryThreshold: '',
    extendedFees: '',
    month: '',
    year: ''
  });

  // Load proposals data to get venue and project dates
  useEffect(() => {
    if (proposalsApiUrl) {
      loadProposals();
    }
  }, [proposalsApiUrl]);

  const loadProposals = async () => {
    if (!proposalsApiUrl) {
      setLoadingProposals(false);
      return;
    }
    
    setLoadingProposals(true);
    try {
      const endpoints = [
        `${proposalsApiUrl}?action=getProposals`,
        `${proposalsApiUrl}?action=getAllProposals`,
        `${proposalsApiUrl}`
      ];
      
      let data = null;
      for (const endpoint of endpoints) {
        try {
          const response = await fetch(endpoint);
          const result = await response.json();
          if (result.success && (result.proposals || result.data)) {
            data = result;
            break;
          }
        } catch (e) {
          continue;
        }
      }
      
      if (data && (data.proposals || data.data)) {
        const proposalsList = data.proposals || data.data || [];
        // Create a map of projectNumber -> proposal data
        const proposalsMap = {};
        proposalsList.forEach(p => {
          if (p.projectNumber) {
            const projectNum = String(p.projectNumber).trim();
            // Keep the latest version if multiple exist
            if (!proposalsMap[projectNum] || (p.version || 1) > (proposalsMap[projectNum].version || 1)) {
              proposalsMap[projectNum] = {
                venueName: p.venueName || '',
                city: p.city || '',
                state: p.state || '',
                startDate: p.startDate || '',
                endDate: p.endDate || ''
              };
            }
          }
        });
        setProposals(proposalsMap);
      }
    } catch (err) {
      console.error('Error loading proposals:', err);
    } finally {
      setLoadingProposals(false);
    }
  };

  // Generate reports automatically when calculations or expenses change
  useEffect(() => {
    generateReports();
  }, [savedCalculations, savedExpenses, proposals]);

  const formatDateRange = (startDate, endDate) => {
    if (!startDate && !endDate) return '';
    
    // Parse a date string as a local date (not UTC) to avoid timezone shifts
    const parseLocalDate = (dateStr) => {
      if (!dateStr) return null;
      
      // If it's already a Date object, extract components directly
      if (dateStr instanceof Date) {
        return {
          year: dateStr.getFullYear(),
          month: dateStr.getMonth(),
          day: dateStr.getDate()
        };
      }
      
      // If it's a YYYY-MM-DD string, parse as local date
      if (typeof dateStr === 'string' && dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
        const [year, month, day] = dateStr.split('-').map(Number);
        return { year, month: month - 1, day }; // month is 0-indexed
      }
      
      // If it's an ISO string, extract date part and parse as local
      if (typeof dateStr === 'string' && dateStr.includes('T')) {
        const datePart = dateStr.split('T')[0];
        const [year, month, day] = datePart.split('-').map(Number);
        return { year, month: month - 1, day };
      }
      
      // Try parsing as Date, but extract local components
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return null;
      return {
        year: date.getFullYear(),
        month: date.getMonth(),
        day: date.getDate()
      };
    };
    
    const formatDate = (dateParts) => {
      if (!dateParts) return '';
      const months = ['January', 'February', 'March', 'April', 'May', 'June', 
                      'July', 'August', 'September', 'October', 'November', 'December'];
      return `${months[dateParts.month]} ${dateParts.day}, ${dateParts.year}`;
    };
    
    const startParts = parseLocalDate(startDate);
    const endParts = parseLocalDate(endDate);
    
    if (!startParts && !endParts) return '';
    if (!endParts) return formatDate(startParts);
    if (!startParts) return formatDate(endParts);
    
    // If same day, just return single date: "October 9, 2026"
    if (startParts.year === endParts.year && 
        startParts.month === endParts.month && 
        startParts.day === endParts.day) {
      return formatDate(startParts);
    }
    
    // If same month and year, format as "November 13-20, 2026"
    if (startParts.month === endParts.month && 
        startParts.year === endParts.year) {
      const months = ['January', 'February', 'March', 'April', 'May', 'June', 
                      'July', 'August', 'September', 'October', 'November', 'December'];
      return `${months[startParts.month]} ${startParts.day}-${endParts.day}, ${startParts.year}`;
    }
    
    // Different months/years: "November 13, 2026 - December 5, 2026"
    return `${formatDate(startParts)} - ${formatDate(endParts)}`;
  };

  const generateReports = () => {
    setLoading(true);

    // Helper function to parse currency values (handles "$1,234.56" format)
    const parseCurrency = (value) => {
      if (!value) return 0;
      if (typeof value === 'number') return value;
      const cleaned = String(value).replace(/[$,\s]/g, '');
      return parseFloat(cleaned) || 0;
    };

    const pickMostRecentCalc = (calculations) =>
      calculations.slice().sort((a, b) => {
        const aVersion = a.version || 1;
        const bVersion = b.version || 1;
        if (bVersion !== aVersion) return bVersion - aVersion;
        const aTime = a.timestamp ? new Date(a.timestamp).getTime() : 0;
        const bTime = b.timestamp ? new Date(b.timestamp).getTime() : 0;
        return bTime - aTime;
      })[0];

    const feeChargedFromCalc = (mostRecentCalc) => {
      const eventType = mostRecentCalc.eventType || 'Standard';
      const nonStandardDelivery = eventType === 'Non-Standard Delivery';
      if (nonStandardDelivery) {
        return parseCurrency(mostRecentCalc.manualCostsTotal);
      }
      const totalFeeFromColumn = parseCurrency(mostRecentCalc.totalFee);
      if (totalFeeFromColumn > 0) return totalFeeFromColumn;
      const standardFeeValue =
        mostRecentCalc.standardFee === 'Waived' || mostRecentCalc.standardFee === 'waived'
          ? 0
          : parseCurrency(mostRecentCalc.standardFee);
      const extendedFeeValue = parseCurrency(mostRecentCalc.extendedFee);
      return standardFeeValue + extendedFeeValue;
    };

    // Group calculations and expenses by project number (individual reports)
    const calcByProject = {};
    savedCalculations.forEach((calc) => {
      if (calc.type === 'program') return;
      const projectNum = calc.projectNumber || '';
      if (!projectNum) return;
      if (!calcByProject[projectNum]) calcByProject[projectNum] = [];
      calcByProject[projectNum].push(calc);
    });

    const expByProject = {};
    savedExpenses.forEach((exp) => {
      if (exp.type === 'program' || exp.programId) return;
      const projectNum = exp.projectNumber || '';
      if (!projectNum) return;
      if (!expByProject[projectNum]) expByProject[projectNum] = [];
      expByProject[projectNum].push(exp);
    });

    // Group program calcs / expenses by programId
    const calcByProgram = {};
    savedCalculations.forEach((calc) => {
      if (calc.type !== 'program') return;
      const programId = String(calc.programId || '').trim();
      if (!programId) return;
      if (!calcByProgram[programId]) calcByProgram[programId] = [];
      calcByProgram[programId].push(calc);
    });

    const expByProgram = {};
    savedExpenses.forEach((exp) => {
      if (exp.type !== 'program' && !exp.programId) return;
      const programId = String(exp.programId || '').trim();
      if (!programId) return;
      if (!expByProgram[programId]) expByProgram[programId] = [];
      expByProgram[programId].push(exp);
    });

    const reportProjects = [];

    // Individual project reports
    const allProjectNumbers = new Set([
      ...Object.keys(calcByProject),
      ...Object.keys(expByProject),
    ]);

    allProjectNumbers.forEach((projectNum) => {
      const calculations = calcByProject[projectNum] || [];
      const expenses = expByProject[projectNum] || [];

      if (calculations.length > 0 && expenses.length > 0) {
        const mostRecentCalc = pickMostRecentCalc(calculations);
        const totalExpenses = expenses.reduce(
          (sum, exp) => sum + (parseFloat(exp.totalCost) || 0),
          0
        );
        const eventType = mostRecentCalc.eventType || 'Standard';
        const nonStandardDelivery = eventType === 'Non-Standard Delivery';
        const totalCharged = feeChargedFromCalc(mostRecentCalc);
        const profitLoss = totalCharged - totalExpenses;
        const standardFee = mostRecentCalc.standardFee;
        const amountToThreshold = mostRecentCalc.amountToThreshold;
        const freeDeliveryThresholdMet =
          standardFee === 'waived' ||
          amountToThreshold === 0 ||
          amountToThreshold === null ||
          amountToThreshold === '';
        const extendedFee = parseFloat(mostRecentCalc.extendedFee) || 0;
        const extendedFeesApplied = extendedFee > 0;
        const proposalData = proposals[projectNum] || {};
        const projectDates = formatDateRange(proposalData.startDate, proposalData.endDate);

        reportProjects.push({
          projectNumber: projectNum,
          reportType: 'project',
          clientName: mostRecentCalc.clientName || expenses[0].clientName || '',
          venue: proposalData.venueName || '',
          city: proposalData.city || '',
          state: proposalData.state || '',
          projectDates: projectDates,
          startDate: proposalData.startDate || '',
          endDate: proposalData.endDate || '',
          deliveryFeeCharged: totalCharged,
          deliveryCostOOP: totalExpenses,
          profitLoss: profitLoss,
          profitMargin: totalCharged > 0 ? (profitLoss / totalCharged) * 100 : 0,
          freeDeliveryThresholdMet: freeDeliveryThresholdMet,
          extendedFeesApplied: extendedFeesApplied,
          nonStandardDelivery: nonStandardDelivery,
          calculation: mostRecentCalc,
          expenses: expenses,
        });
      }
    });

    // Program reports — matched by programId
    const allProgramIds = new Set([
      ...Object.keys(calcByProgram),
      ...Object.keys(expByProgram),
    ]);

    allProgramIds.forEach((programId) => {
      const calculations = calcByProgram[programId] || [];
      const expenses = expByProgram[programId] || [];
      if (calculations.length === 0 || expenses.length === 0) return;

      const mostRecentCalc = pickMostRecentCalc(calculations);
      const totalExpenses = expenses.reduce(
        (sum, exp) => sum + (parseFloat(exp.totalCost) || 0),
        0
      );
      const totalCharged = feeChargedFromCalc(mostRecentCalc);
      const profitLoss = totalCharged - totalExpenses;
      const standardFee = mostRecentCalc.standardFee;
      const amountToThreshold = mostRecentCalc.amountToThreshold;
      const freeDeliveryThresholdMet =
        standardFee === 'waived' ||
        amountToThreshold === 0 ||
        amountToThreshold === null ||
        amountToThreshold === '';
      const extendedFee = parseFloat(mostRecentCalc.extendedFee) || 0;
      const pd = mostRecentCalc.programData || {};
      const includedCount =
        pd.includedCount != null
          ? pd.includedCount
          : Array.isArray(pd.includedEvents)
            ? pd.includedEvents.length
            : null;

      reportProjects.push({
        projectNumber: programId,
        reportType: 'program',
        programId,
        programName: mostRecentCalc.programName || expenses[0].programName || programId,
        clientName:
          mostRecentCalc.clientName ||
          expenses[0].clientName ||
          mostRecentCalc.programName ||
          programId,
        venue: includedCount != null ? `${includedCount} Events` : 'Program',
        city: '',
        state: '',
        projectDates: '',
        startDate: '',
        endDate: '',
        deliveryFeeCharged: totalCharged,
        deliveryCostOOP: totalExpenses,
        profitLoss: profitLoss,
        profitMargin: totalCharged > 0 ? (profitLoss / totalCharged) * 100 : 0,
        freeDeliveryThresholdMet: freeDeliveryThresholdMet,
        extendedFeesApplied: extendedFee > 0,
        nonStandardDelivery: false,
        calculation: mostRecentCalc,
        expenses: expenses,
      });
    });

    // Sort by project/program id (descending) by default
    reportProjects.sort((a, b) => {
      const aNum = parseInt(a.projectNumber);
      const bNum = parseInt(b.projectNumber);
      if (!isNaN(aNum) && !isNaN(bNum)) {
        return bNum - aNum;
      }
      return String(b.projectNumber).localeCompare(String(a.projectNumber));
    });

    setReports(reportProjects);
    applyFiltersAndSort(reportProjects);
    setLoading(false);
  };

  // Apply filters and sorting
  const applyFiltersAndSort = (reportsToFilter = reports) => {
    let filtered = [...reportsToFilter];

    // Apply filters
    if (filters.city) {
      filtered = filtered.filter(r => 
        r.city.toLowerCase().includes(filters.city.toLowerCase())
      );
    }
    if (filters.state) {
      filtered = filtered.filter(r => 
        r.state.toLowerCase().includes(filters.state.toLowerCase())
      );
    }
    if (filters.clientName) {
      filtered = filtered.filter(r => 
        r.clientName.toLowerCase().includes(filters.clientName.toLowerCase())
      );
    }
    if (filters.projectNumber) {
      const q = filters.projectNumber.toLowerCase();
      filtered = filtered.filter((r) =>
        String(r.projectNumber || '').toLowerCase().includes(q) ||
        String(r.programName || '').toLowerCase().includes(q) ||
        String(r.programId || '').toLowerCase().includes(q)
      );
    }
    if (filters.month || filters.year) {
      filtered = filtered.filter(r => {
        const startDate = r.startDate;
        const endDate = r.endDate;
        
        if (!startDate && !endDate) return false;
        
        // Parse dates - they should be in YYYY-MM-DD format
        const parseDate = (dateStr) => {
          if (!dateStr) return null;
          if (typeof dateStr === 'string' && dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
            const [year, month, day] = dateStr.split('-').map(Number);
            return { year, month, day };
          }
          const date = new Date(dateStr);
          if (!isNaN(date.getTime())) {
            return {
              year: date.getFullYear(),
              month: date.getMonth() + 1,
              day: date.getDate()
            };
          }
          return null;
        };
        
        const start = parseDate(startDate);
        const end = parseDate(endDate);
        
        if (!start && !end) return false;
        
        const selectedMonth = filters.month ? parseInt(filters.month) : null;
        const selectedYear = filters.year ? parseInt(filters.year) : null;
        
        // If both month and year are selected
        if (selectedMonth && selectedYear) {
          if (start && start.year === selectedYear && start.month === selectedMonth) return true;
          if (end && end.year === selectedYear && end.month === selectedMonth) return true;
          if (start && end) {
            const startDateObj = new Date(start.year, start.month - 1, start.day);
            const endDateObj = new Date(end.year, end.month - 1, end.day);
            const filterStart = new Date(selectedYear, selectedMonth - 1, 1);
            const filterEnd = new Date(selectedYear, selectedMonth, 0);
            return startDateObj <= filterEnd && endDateObj >= filterStart;
          }
        } else if (selectedYear) {
          // Only year selected
          if (start && start.year === selectedYear) return true;
          if (end && end.year === selectedYear) return true;
          if (start && end) {
            return start.year <= selectedYear && end.year >= selectedYear;
          }
        } else if (selectedMonth) {
          // Only month selected
          if (start && start.month === selectedMonth) return true;
          if (end && end.month === selectedMonth) return true;
        }
        
        return false;
      });
    }

    // Apply sorting
    if (sortConfig.key) {
      filtered.sort((a, b) => {
        let aVal = a[sortConfig.key];
        let bVal = b[sortConfig.key];

        // Handle numeric sorting
        if (sortConfig.key === 'deliveryFeeCharged' || 
            sortConfig.key === 'deliveryCostOOP' || 
            sortConfig.key === 'profitLoss' ||
            sortConfig.key === 'profitMargin') {
          aVal = parseFloat(aVal) || 0;
          bVal = parseFloat(bVal) || 0;
        }
        // Handle boolean sorting
        else if (sortConfig.key === 'freeDeliveryThresholdMet' || 
                 sortConfig.key === 'extendedFeesApplied' ||
                 sortConfig.key === 'nonStandardDelivery') {
          // Convert boolean to number for sorting (true = 1, false = 0)
          aVal = aVal ? 1 : 0;
          bVal = bVal ? 1 : 0;
        }
        // Handle project number sorting
        else if (sortConfig.key === 'projectNumber') {
          const aNum = parseInt(aVal);
          const bNum = parseInt(bVal);
          if (!isNaN(aNum) && !isNaN(bNum)) {
            aVal = aNum;
            bVal = bNum;
          }
        }
        // Handle string sorting
        else {
          aVal = String(aVal || '').toLowerCase();
          bVal = String(bVal || '').toLowerCase();
        }

        if (aVal < bVal) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aVal > bVal) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }

    setFilteredReports(filtered);
  };

  // Handle column sorting
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Handle filter changes
  const handleFilterChange = (filterKey, value) => {
    const newFilters = { ...filters, [filterKey]: value };
    setFilters(newFilters);
  };

  // Re-apply filters and sort when filters or sortConfig change
  useEffect(() => {
    if (reports.length > 0) {
      applyFiltersAndSort();
    }
  }, [filters, sortConfig]);
  
  // Also re-apply when reports change
  useEffect(() => {
    if (reports.length > 0) {
      applyFiltersAndSort();
    }
  }, [reports]);

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '$0.00';
    return `$${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const toggleRowExpansion = (projectNumber) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(projectNumber)) {
      newExpanded.delete(projectNumber);
    } else {
      newExpanded.add(projectNumber);
    }
    setExpandedRows(newExpanded);
  };

  const calculateCategoryComparison = (report) => {
    const calc = report.calculation;
    const expenses = report.expenses || [];
    
    // Get estimated costs from calculation
    const estimated = calc.manualCosts || {};
    
    // Sum actual costs from all expense reports
    const actual = {
      lodging: { total: 0 },
      gas: { total: 0 },
      flights: { total: 0 },
      perDiem: { total: 0 },
      truckRental: { total: 0 },
      other: { total: 0 }
    };
    
    expenses.forEach(exp => {
      if (exp.lodging && exp.lodging.total) {
        actual.lodging.total += parseFloat(exp.lodging.total) || 0;
      }
      if (exp.gas && exp.gas.total) {
        actual.gas.total += parseFloat(exp.gas.total) || 0;
      }
      if (exp.flights && exp.flights.total) {
        actual.flights.total += parseFloat(exp.flights.total) || 0;
      }
      if (exp.perDiem && exp.perDiem.total) {
        actual.perDiem.total += parseFloat(exp.perDiem.total) || 0;
      }
      if (exp.truckRental && exp.truckRental.total) {
        actual.truckRental.total += parseFloat(exp.truckRental.total) || 0;
      }
      if (exp.otherExpenses && Array.isArray(exp.otherExpenses)) {
        exp.otherExpenses.forEach(oe => {
          actual.other.total += parseFloat(oe.total) || 0;
        });
      }
    });
    
    // Calculate differences
    const categories = [
      {
        name: 'Lodging',
        estimated: parseFloat(estimated.lodging?.total) || 0,
        actual: actual.lodging.total,
        difference: (parseFloat(estimated.lodging?.total) || 0) - actual.lodging.total
      },
      {
        name: 'Gas',
        estimated: parseFloat(estimated.gas?.total) || 0,
        actual: actual.gas.total,
        difference: (parseFloat(estimated.gas?.total) || 0) - actual.gas.total
      },
      {
        name: 'Flights',
        estimated: parseFloat(estimated.flights?.total) || 0,
        actual: actual.flights.total,
        difference: (parseFloat(estimated.flights?.total) || 0) - actual.flights.total
      },
      {
        name: 'Per Diem / Day Rate',
        estimated: parseFloat(estimated.perDiem?.total) || 0,
        actual: actual.perDiem.total,
        difference: (parseFloat(estimated.perDiem?.total) || 0) - actual.perDiem.total
      },
      {
        name: 'Truck Rental',
        estimated: parseFloat(estimated.truckRental?.total) || 0,
        actual: actual.truckRental.total,
        difference: (parseFloat(estimated.truckRental?.total) || 0) - actual.truckRental.total
      },
      {
        name: 'Other Expenses',
        estimated: (estimated.otherExpenses || []).reduce((sum, oe) => sum + (parseFloat(oe.total) || 0), 0),
        actual: actual.other.total,
        difference: (estimated.otherExpenses || []).reduce((sum, oe) => sum + (parseFloat(oe.total) || 0), 0) - actual.other.total
      }
    ];
    
    const totalEstimated = categories.reduce((sum, cat) => sum + cat.estimated, 0);
    const totalActual = categories.reduce((sum, cat) => sum + cat.actual, 0);
    const totalDifference = totalEstimated - totalActual;
    
    return { categories, totalEstimated, totalActual, totalDifference };
  };

  return (
    <div>
      <div>
        {loading && (
          <div className="loading">
            <div className="spinner"></div>
            <p>Loading reports...</p>
          </div>
        )}

        {!loading && reports.length === 0 && (
          <div style={{ 
            padding: '24px', 
            backgroundColor: 'var(--brand-off-white)', 
            borderRadius: '8px',
            textAlign: 'center',
            color: 'rgb(51, 51, 51)',
            fontSize: '14px'
          }}>
            No projects found with both calculations and expenses. Submit a calculation and expense report for the same project to see it here.
          </div>
        )}

        {!loading && reports.length > 0 && (
          <>
            {/* Filter Section */}
            <div style={{ 
              marginBottom: '24px',
              padding: '16px',
              backgroundColor: 'var(--brand-off-white)',
              borderRadius: '8px',
              display: 'grid',
              gridTemplateColumns: 'repeat(4, 1fr)',
              gap: '12px'
            }}>
              <div>
                <label style={{ 
                  display: 'block',
                  fontSize: '12px',
                  fontWeight: '600',
                  marginBottom: '6px',
                  color: 'var(--brand-charcoal)'
                }}>Filter by City</label>
                <input
                  type="text"
                  value={filters.city}
                  onChange={(e) => handleFilterChange('city', e.target.value)}
                  placeholder="e.g., Charleston"
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    fontSize: '14px',
                    border: '1px solid var(--brand-light-border)',
                    borderRadius: '6px',
                    fontFamily: 'inherit'
                  }}
                />
              </div>
              <div>
                <label style={{ 
                  display: 'block',
                  fontSize: '12px',
                  fontWeight: '600',
                  marginBottom: '6px',
                  color: 'var(--brand-charcoal)'
                }}>Filter by State</label>
                <input
                  type="text"
                  value={filters.state}
                  onChange={(e) => handleFilterChange('state', e.target.value)}
                  placeholder="e.g., SC"
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    fontSize: '14px',
                    border: '1px solid var(--brand-light-border)',
                    borderRadius: '6px',
                    fontFamily: 'inherit'
                  }}
                />
              </div>
              <div>
                <label style={{ 
                  display: 'block',
                  fontSize: '12px',
                  fontWeight: '600',
                  marginBottom: '6px',
                  color: 'var(--brand-charcoal)'
                }}>Filter by Client</label>
                <input
                  type="text"
                  value={filters.clientName}
                  onChange={(e) => handleFilterChange('clientName', e.target.value)}
                  placeholder="Client name"
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    fontSize: '14px',
                    border: '1px solid var(--brand-light-border)',
                    borderRadius: '6px',
                    fontFamily: 'inherit'
                  }}
                />
              </div>
              <div>
                <label style={{ 
                  display: 'block',
                  fontSize: '12px',
                  fontWeight: '600',
                  marginBottom: '6px',
                  color: 'var(--brand-charcoal)'
                }}>Filter by Project #</label>
                <input
                  type="text"
                  value={filters.projectNumber}
                  onChange={(e) => handleFilterChange('projectNumber', e.target.value)}
                  placeholder="Project number"
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    fontSize: '14px',
                    border: '1px solid var(--brand-light-border)',
                    borderRadius: '6px',
                    fontFamily: 'inherit'
                  }}
                />
              </div>
              <div>
                <label style={{ 
                  display: 'block',
                  fontSize: '12px',
                  fontWeight: '600',
                  marginBottom: '6px',
                  color: 'var(--brand-charcoal)'
                }}>Filter by Free Delivery Threshold</label>
                <select
                  value={filters.freeDeliveryThreshold}
                  onChange={(e) => handleFilterChange('freeDeliveryThreshold', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    fontSize: '14px',
                    border: '1px solid var(--brand-light-border)',
                    borderRadius: '6px',
                    fontFamily: 'inherit',
                    backgroundColor: 'white'
                  }}
                >
                  <option value="">All</option>
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>
              </div>
              <div>
                <label style={{ 
                  display: 'block',
                  fontSize: '12px',
                  fontWeight: '600',
                  marginBottom: '6px',
                  color: 'var(--brand-charcoal)'
                }}>Filter by Extended Fees</label>
                <select
                  value={filters.extendedFees}
                  onChange={(e) => handleFilterChange('extendedFees', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    fontSize: '14px',
                    border: '1px solid var(--brand-light-border)',
                    borderRadius: '6px',
                    fontFamily: 'inherit',
                    backgroundColor: 'white'
                  }}
                >
                  <option value="">All</option>
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>
              </div>
              <div>
                <label style={{ 
                  display: 'block',
                  fontSize: '12px',
                  fontWeight: '600',
                  marginBottom: '6px',
                  color: 'var(--brand-charcoal)'
                }}>Filter by Month</label>
                <select
                  value={filters.month}
                  onChange={(e) => handleFilterChange('month', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    fontSize: '14px',
                    border: '1px solid var(--brand-light-border)',
                    borderRadius: '6px',
                    fontFamily: 'inherit',
                    backgroundColor: 'white'
                  }}
                >
                  <option value="">All Months</option>
                  <option value="1">January</option>
                  <option value="2">February</option>
                  <option value="3">March</option>
                  <option value="4">April</option>
                  <option value="5">May</option>
                  <option value="6">June</option>
                  <option value="7">July</option>
                  <option value="8">August</option>
                  <option value="9">September</option>
                  <option value="10">October</option>
                  <option value="11">November</option>
                  <option value="12">December</option>
                </select>
              </div>
              <div>
                <label style={{ 
                  display: 'block',
                  fontSize: '12px',
                  fontWeight: '600',
                  marginBottom: '6px',
                  color: 'var(--brand-charcoal)'
                }}>Filter by Year</label>
                <select
                  value={filters.year}
                  onChange={(e) => handleFilterChange('year', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    fontSize: '14px',
                    border: '1px solid var(--brand-light-border)',
                    borderRadius: '6px',
                    fontFamily: 'inherit',
                    backgroundColor: 'white'
                  }}
                >
                  <option value="">All Years</option>
                  {(() => {
                    const currentYear = new Date().getFullYear();
                    const years = [];
                    // Generate years from 2026 to current year + 2
                    for (let y = 2026; y <= currentYear + 2; y++) {
                      years.push(y);
                    }
                    return years.reverse().map(year => (
                      <option key={year} value={year.toString()}>{year}</option>
                    ));
                  })()}
                </select>
              </div>
            </div>

            {/* Results count */}
            {filteredReports.length !== reports.length && (
              <div style={{ 
                marginBottom: '12px',
                fontSize: '14px',
                color: 'rgb(51, 51, 51)'
              }}>
                Showing {filteredReports.length} of {reports.length} projects
              </div>
            )}

            <div style={{ overflowX: 'auto' }}>
              <table style={{ 
                width: '100%', 
                borderCollapse: 'collapse',
                marginTop: '16px'
              }}>
                <thead>
                  <tr style={{ 
                    borderBottom: '2px solid var(--brand-charcoal)',
                    textAlign: 'left'
                  }}>
                    <th 
                      onClick={() => handleSort('clientName')}
                      style={{ 
                        padding: '12px 16px', 
                        fontSize: '12px', 
                        fontWeight: '600',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        color: 'var(--brand-charcoal)',
                        cursor: 'pointer',
                        userSelect: 'none',
                        position: 'relative'
                      }}
                    >
                      Client Name
                      {sortConfig.key === 'clientName' && (
                        <span style={{ marginLeft: '6px' }}>
                          {sortConfig.direction === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </th>
                    <th 
                      onClick={() => handleSort('projectNumber')}
                      style={{ 
                        padding: '12px 16px', 
                        fontSize: '12px', 
                        fontWeight: '600',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        color: 'var(--brand-charcoal)',
                        cursor: 'pointer',
                        userSelect: 'none'
                      }}
                    >
                      Project Number
                      {sortConfig.key === 'projectNumber' && (
                        <span style={{ marginLeft: '6px' }}>
                          {sortConfig.direction === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </th>
                    <th 
                      onClick={() => handleSort('venue')}
                      style={{ 
                        padding: '12px 16px', 
                        fontSize: '12px', 
                        fontWeight: '600',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        color: 'var(--brand-charcoal)',
                        cursor: 'pointer',
                        userSelect: 'none'
                      }}
                    >
                      Venue
                      {sortConfig.key === 'venue' && (
                        <span style={{ marginLeft: '6px' }}>
                          {sortConfig.direction === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </th>
                    <th 
                      onClick={() => handleSort('city')}
                      style={{ 
                        padding: '12px 16px', 
                        fontSize: '12px', 
                        fontWeight: '600',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        color: 'var(--brand-charcoal)',
                        cursor: 'pointer',
                        userSelect: 'none'
                      }}
                    >
                      City
                      {sortConfig.key === 'city' && (
                        <span style={{ marginLeft: '6px' }}>
                          {sortConfig.direction === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </th>
                    <th 
                      onClick={() => handleSort('state')}
                      style={{ 
                        padding: '12px 16px', 
                        fontSize: '12px', 
                        fontWeight: '600',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        color: 'var(--brand-charcoal)',
                        cursor: 'pointer',
                        userSelect: 'none'
                      }}
                    >
                      State
                      {sortConfig.key === 'state' && (
                        <span style={{ marginLeft: '6px' }}>
                          {sortConfig.direction === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </th>
                    <th style={{ 
                      padding: '12px 16px', 
                      fontSize: '12px', 
                      fontWeight: '600',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      color: 'var(--brand-charcoal)'
                    }}>Project Date(s)</th>
                    <th 
                      onClick={() => handleSort('deliveryFeeCharged')}
                      style={{ 
                        padding: '12px 16px', 
                        fontSize: '12px', 
                        fontWeight: '600',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        color: 'var(--brand-charcoal)',
                        cursor: 'pointer',
                        userSelect: 'none'
                      }}
                    >
                      Delivery Fee (Charged)
                      {sortConfig.key === 'deliveryFeeCharged' && (
                        <span style={{ marginLeft: '6px' }}>
                          {sortConfig.direction === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </th>
                    <th 
                      onClick={() => handleSort('deliveryCostOOP')}
                      style={{ 
                        padding: '12px 16px', 
                        fontSize: '12px', 
                        fontWeight: '600',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        color: 'var(--brand-charcoal)',
                        cursor: 'pointer',
                        userSelect: 'none'
                      }}
                    >
                      Delivery Cost (OOP)
                      {sortConfig.key === 'deliveryCostOOP' && (
                        <span style={{ marginLeft: '6px' }}>
                          {sortConfig.direction === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </th>
                    <th 
                      onClick={() => handleSort('profitLoss')}
                      style={{ 
                        padding: '12px 16px', 
                        fontSize: '12px', 
                        fontWeight: '600',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        color: 'var(--brand-charcoal)',
                        cursor: 'pointer',
                        userSelect: 'none'
                      }}
                    >
                      Profit/Loss
                      {sortConfig.key === 'profitLoss' && (
                        <span style={{ marginLeft: '6px' }}>
                          {sortConfig.direction === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </th>
                    <th 
                      onClick={() => handleSort('freeDeliveryThresholdMet')}
                      style={{ 
                        padding: '12px 16px', 
                        fontSize: '12px', 
                        fontWeight: '600',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        color: 'var(--brand-charcoal)',
                        cursor: 'pointer',
                        userSelect: 'none'
                      }}
                    >
                      Free Delivery Threshold Met
                      {sortConfig.key === 'freeDeliveryThresholdMet' && (
                        <span style={{ marginLeft: '6px' }}>
                          {sortConfig.direction === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </th>
                    <th 
                      onClick={() => handleSort('extendedFeesApplied')}
                      style={{ 
                        padding: '12px 16px', 
                        fontSize: '12px', 
                        fontWeight: '600',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        color: 'var(--brand-charcoal)',
                        cursor: 'pointer',
                        userSelect: 'none'
                      }}
                    >
                      Extended Fees Applied
                      {sortConfig.key === 'extendedFeesApplied' && (
                        <span style={{ marginLeft: '6px' }}>
                          {sortConfig.direction === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </th>
                    <th 
                      onClick={() => handleSort('nonStandardDelivery')}
                      style={{ 
                        padding: '12px 16px', 
                        fontSize: '12px', 
                        fontWeight: '600',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        color: 'var(--brand-charcoal)',
                        cursor: 'pointer',
                        userSelect: 'none'
                      }}
                    >
                      Non-Standard Delivery
                      {sortConfig.key === 'nonStandardDelivery' && (
                        <span style={{ marginLeft: '6px' }}>
                          {sortConfig.direction === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredReports.length === 0 ? (
                    <tr>
                      <td colSpan="12" style={{ 
                        padding: '24px',
                        textAlign: 'center',
                        color: 'rgb(51, 51, 51)',
                        fontSize: '14px'
                      }}>
                        No projects match the current filters.
                      </td>
                    </tr>
                  ) : (
                    filteredReports.map((report, index) => (
                      <React.Fragment key={index}>
                        <tr 
                          style={{ 
                            borderBottom: '1px solid var(--brand-light-border)',
                            backgroundColor: index % 2 === 0 ? 'white' : 'var(--brand-off-white)'
                          }}
                        >
                        <td style={{ 
                          padding: '12px 16px', 
                          fontSize: '14px',
                          color: 'rgb(51, 51, 51)'
                        }}>{report.clientName}</td>
                        <td style={{ 
                          padding: '12px 16px', 
                          fontSize: '14px',
                          color: 'rgb(51, 51, 51)',
                          fontFamily: 'monospace'
                        }}>
                          {report.reportType === 'program'
                            ? (report.programName || report.programId || report.projectNumber)
                            : report.projectNumber}
                        </td>
                        <td style={{ 
                          padding: '12px 16px', 
                          fontSize: '14px',
                          color: 'rgb(51, 51, 51)'
                        }}>
                          {loadingProposals ? (
                            <span style={{ color: 'rgb(136, 136, 136)', fontStyle: 'italic', fontSize: '12px' }}>Loading...</span>
                          ) : (report.venue || '—')}
                        </td>
                        <td style={{ 
                          padding: '12px 16px', 
                          fontSize: '14px',
                          color: 'rgb(51, 51, 51)'
                        }}>
                          {loadingProposals ? (
                            <span style={{ color: 'rgb(136, 136, 136)', fontStyle: 'italic', fontSize: '12px' }}>Loading...</span>
                          ) : (report.city || '—')}
                        </td>
                        <td style={{ 
                          padding: '12px 16px', 
                          fontSize: '14px',
                          color: 'rgb(51, 51, 51)'
                        }}>
                          {loadingProposals ? (
                            <span style={{ color: 'rgb(136, 136, 136)', fontStyle: 'italic', fontSize: '12px' }}>Loading...</span>
                          ) : (report.state || '—')}
                        </td>
                        <td style={{ 
                          padding: '12px 16px', 
                          fontSize: '14px',
                          color: 'rgb(51, 51, 51)'
                        }}>
                          {loadingProposals ? (
                            <span style={{ color: 'rgb(136, 136, 136)', fontStyle: 'italic', fontSize: '12px' }}>Loading...</span>
                          ) : (report.projectDates || '—')}
                        </td>
                        <td style={{ 
                          padding: '12px 16px', 
                          fontSize: '14px',
                          color: 'rgb(51, 51, 51)',
                          fontWeight: '500'
                        }}>{formatCurrency(report.deliveryFeeCharged)}</td>
                        <td style={{ 
                          padding: '12px 16px', 
                          fontSize: '14px',
                          color: 'rgb(51, 51, 51)',
                          fontWeight: '500'
                        }}>{formatCurrency(report.deliveryCostOOP)}</td>
                        <td style={{ 
                          padding: '12px 16px', 
                          fontSize: '14px',
                          fontWeight: '600',
                          color: report.profitLoss >= 0 ? '#16a34a' : '#dc2626'
                        }}>
                          {report.profitLoss >= 0 ? '+' : ''}{formatCurrency(report.profitLoss)}
                          <span style={{ 
                            fontSize: '12px', 
                            marginLeft: '8px',
                            opacity: 0.7
                          }}>
                            ({report.profitMargin.toFixed(1)}%)
                          </span>
                        </td>
                        <td style={{ 
                          padding: '12px 16px', 
                          fontSize: '14px',
                          color: 'rgb(51, 51, 51)',
                          textAlign: 'center'
                        }}>
                          {report.freeDeliveryThresholdMet ? 'Yes' : 'No'}
                        </td>
                        <td style={{ 
                          padding: '12px 16px', 
                          fontSize: '14px',
                          color: 'rgb(51, 51, 51)',
                          textAlign: 'center'
                        }}>
                          {report.extendedFeesApplied ? 'Yes' : 'No'}
                        </td>
                        <td style={{ 
                          padding: '12px 16px', 
                          fontSize: '14px',
                          color: 'rgb(51, 51, 51)',
                          textAlign: 'center'
                        }}>
                          {report.nonStandardDelivery ? (
                            <button
                              onClick={() => toggleRowExpansion(report.projectNumber)}
                              style={{
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                color: 'var(--brand-charcoal)',
                                fontSize: '14px',
                                padding: '4px 8px',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '6px'
                              }}
                            >
                              <span>{expandedRows.has(report.projectNumber) ? '▼' : '▶'}</span>
                              <span>Yes</span>
                            </button>
                          ) : (
                            'No'
                          )}
                        </td>
                      </tr>
                      {report.nonStandardDelivery && expandedRows.has(report.projectNumber) && (
                        <tr>
                          <td colSpan="12" style={{ 
                            padding: '24px',
                            backgroundColor: 'var(--brand-off-white)',
                            borderBottom: '2px solid var(--brand-light-border)'
                          }}>
                            <div style={{ marginBottom: '16px' }}>
                              {(() => {
                                const comparison = calculateCategoryComparison(report);
                                const firstExpense = report.expenses && report.expenses.length > 0 ? report.expenses[0] : null;
                                const reportPreparedBy = firstExpense?.reportPreparedBy || '';
                                const deliverySupervisor = firstExpense?.deliverySupervisor || '';
                                
                                return (
                                  <div>
                                    {/* Header - left-aligned with Category column */}
                                    <h4 style={{ 
                                      fontSize: '16px', 
                                      fontWeight: '200', 
                                      color: 'var(--brand-charcoal)',
                                      marginBottom: '16px',
                                      marginLeft: '12px',
                                      fontFamily: "'Domaine Text', serif"
                                    }}>
                                      Estimated vs. Actual Costs Breakdown
                                    </h4>
                                    
                                    <table style={{ 
                                      width: '100%', 
                                      borderCollapse: 'collapse',
                                      marginBottom: '20px'
                                    }}>
                                      <thead>
                                        <tr style={{ 
                                          borderBottom: '1px solid var(--brand-light-border)',
                                          textAlign: 'left'
                                        }}>
                                          <th style={{ 
                                            padding: '8px 12px', 
                                            fontSize: '12px', 
                                            fontWeight: '600',
                                            textTransform: 'uppercase',
                                            color: 'var(--brand-charcoal)'
                                          }}>Category</th>
                                          <th style={{ 
                                            padding: '8px 12px', 
                                            fontSize: '12px', 
                                            fontWeight: '600',
                                            textTransform: 'uppercase',
                                            color: 'var(--brand-charcoal)',
                                            textAlign: 'right'
                                          }}>Estimated</th>
                                          <th style={{ 
                                            padding: '8px 12px', 
                                            fontSize: '12px', 
                                            fontWeight: '600',
                                            textTransform: 'uppercase',
                                            color: 'var(--brand-charcoal)',
                                            textAlign: 'right'
                                          }}>Actual</th>
                                          <th style={{ 
                                            padding: '8px 12px', 
                                            fontSize: '12px', 
                                            fontWeight: '600',
                                            textTransform: 'uppercase',
                                            color: 'var(--brand-charcoal)',
                                            textAlign: 'right'
                                          }}>Difference</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {comparison.categories.map((cat, idx) => (
                                          <tr key={idx} style={{ 
                                            borderBottom: '1px solid var(--brand-light-border)'
                                          }}>
                                            <td style={{ 
                                              padding: '10px 12px', 
                                              fontSize: '14px',
                                              color: 'rgb(51, 51, 51)'
                                            }}>{cat.name}</td>
                                            <td style={{ 
                                              padding: '10px 12px', 
                                              fontSize: '14px',
                                              color: 'rgb(51, 51, 51)',
                                              textAlign: 'right'
                                            }}>{formatCurrency(cat.estimated)}</td>
                                            <td style={{ 
                                              padding: '10px 12px', 
                                              fontSize: '14px',
                                              color: 'rgb(51, 51, 51)',
                                              textAlign: 'right'
                                            }}>{formatCurrency(cat.actual)}</td>
                                            <td style={{ 
                                              padding: '10px 12px', 
                                              fontSize: '14px',
                                              fontWeight: '500',
                                              color: cat.difference >= 0 ? '#16a34a' : '#dc2626',
                                              textAlign: 'right'
                                            }}>
                                              {cat.difference >= 0 ? '+' : ''}{formatCurrency(cat.difference)}
                                            </td>
                                          </tr>
                                        ))}
                                        <tr style={{ 
                                          borderTop: '2px solid var(--brand-charcoal)'
                                        }}>
                                          <td style={{ 
                                            padding: '12px',
                                            fontSize: '14px',
                                            fontWeight: '600',
                                            color: 'var(--brand-charcoal)'
                                          }}>Total</td>
                                          <td style={{ 
                                            padding: '12px',
                                            fontSize: '14px',
                                            fontWeight: '600',
                                            color: 'var(--brand-charcoal)',
                                            textAlign: 'right'
                                          }}>{formatCurrency(comparison.totalEstimated)}</td>
                                          <td style={{ 
                                            padding: '12px',
                                            fontSize: '14px',
                                            fontWeight: '600',
                                            color: 'var(--brand-charcoal)',
                                            textAlign: 'right'
                                          }}>{formatCurrency(comparison.totalActual)}</td>
                                          <td style={{ 
                                            padding: '12px',
                                            fontSize: '14px',
                                            fontWeight: '600',
                                            color: comparison.totalDifference >= 0 ? '#16a34a' : '#dc2626',
                                            textAlign: 'right'
                                          }}>
                                            {comparison.totalDifference >= 0 ? '+' : ''}{formatCurrency(comparison.totalDifference)}
                                          </td>
                                        </tr>
                                      </tbody>
                                    </table>
                                    
                                    {/* Prepared by and Delivery Supervisor - below totals, left-aligned with Category column */}
                                    {(reportPreparedBy || deliverySupervisor) && (
                                      <div style={{ 
                                        marginTop: '20px',
                                        marginLeft: '12px',
                                        fontSize: '14px',
                                        color: 'rgb(51, 51, 51)',
                                        fontFamily: "'Neue Haas Unica', Inter, sans-serif"
                                      }}>
                                        {reportPreparedBy && (
                                          <div style={{ marginBottom: '8px' }}>
                                            <strong>Prepared by:</strong> {reportPreparedBy}
                                          </div>
                                        )}
                                        {deliverySupervisor && (
                                          <div>
                                            <strong>Delivery Supervisor:</strong> {deliverySupervisor}
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                );
                              })()}
                            </div>
                          </td>
                        </tr>
                      )}
                      </React.Fragment>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default Comparison;
